import React from 'react';
import { Box, Typography, Row, Col, Accordion } from '@lucid/lego';
import type { Theme, VehicleData } from '../types';
import styles from './CustomerProfile.module.scss';

type CustomerProfileProps = {
  /** Customer data from vehicle */
  customer: VehicleData['customer'];
  /** Theme for the component */
  theme: Theme;
};

/**
 * Customer profile accordion section displaying customer information
 * @component
 * @param {CustomerProfileProps} props - Component props
 * @returns {JSX.Element} Accordion with customer contact details
 * @example
 * <CustomerProfile customer={vehicleData.customer} theme="dark" />
 */
const CustomerProfile: React.FC<CustomerProfileProps> = ({ customer, theme }) => {
  const renderField = (label: string, value: string) => (
    <Col xl={6} lg={6} key={label}>
      <Typography variant="label" size="small" theme={theme} color="gray-5">
        {label}
      </Typography>
      <Box mt={4}>
        <Typography variant="body" size="small" theme={theme} color="white">
          {value || '-'}
        </Typography>
      </Box>
    </Col>
  );

  return (
    <Accordion title="Customer" theme={theme} defaultOpen={true} showBorder={true}>
      <Box className={styles.customerSection}>
        <Typography variant="label" size="medium" theme={theme} color="white">
          Customer Profile
        </Typography>

        <Box mt={16} className={styles.customerDetails} data-theme={theme}>
          <Row gutter={[8, 16]}>
            {renderField('Name', customer.name)}
            {renderField('Street', customer.street)}
            {renderField('Phone', customer.phone)}
            {renderField('City', customer.city)}
            {renderField('Email', customer.email)}
            {renderField('Postal', customer.postal)}
            <Col xl={6} lg={6} />
            {renderField('Country', customer.country)}
          </Row>
        </Box>
      </Box>
    </Accordion>
  );
};

export default CustomerProfile;
